import React, { useState, useEffect } from "react";
import { Modal, Input, Select, Form, message } from "antd";
import { createCourse, updateCourse } from "../../../service/AdminService/BookService";
import {
  validateMinLength,
  validateNotEmpty,
} from "../../../utility/validation";
import CustomButton from "../../shared/button/CustomButton";
import CustomModal from "../../shared/CustomModal/CustomModal";
import InputWrapper from "../../shared/Input/Input-Wrapper";
import CustomInput from "../../shared/Input/Custom-Input";
import CustomSelector from "../../shared/Input/Custom-Selector";

const { Option } = Select;

const CoursesModal = ({
  title,
  isModalOpen,
  handleCloseModal,
  selectedCourse,
  setLoading,
  loadCourses,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (isModalOpen) {
      if (selectedCourse) {
        form.setFieldsValue({
          title: selectedCourse.title,
          description: selectedCourse.description,
          ownerId: selectedCourse.ownerId,
          courseLevel: selectedCourse.level || "",
          isActive: selectedCourse.active ? "true" : "false",
        });
      } else {
        form.resetFields();
      }
    }
  }, [isModalOpen, selectedCourse, form]);

  const handleSubmit = async (values) => {
    const coursePayload = {
      title: values.title,
      ownerId: parseInt(values.ownerId, 10),
      description: values.description,
      courseLevel: values.courseLevel,
      isActive: values.Status === "true",
    };

    setLoading(true);
    try {
      if (selectedCourse) {
        const res = await updateCourse(selectedCourse.courseId, coursePayload);
        message.success(res?.message || "Course updated successfully!");
      } else {
        await createCourse(coursePayload);
        message.success("Course added successfully!");
      }

      await loadCourses();
    } catch (error) {
      message.error(error?.message || "Something went wrong!");
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  const validateForm = (rule, value) => {
    if (!validateNotEmpty(value)) {
      return Promise.reject("This field is required!");
    }
    if (rule.field === "title" && !validateMinLength(value, 3)) {
      return Promise.reject("Title should be at least 3 characters!");
    }
    if (rule.field === "ownerId" && isNaN(value)) {
      return Promise.reject("Owner ID must be numeric!");
    }
    return Promise.resolve();
  };

  return (
    <CustomModal
      visible={isModalOpen}
      onClose={handleCloseModal}
      title={selectedCourse ? "Edit Course" : "Add New Course"}
      destroyOnClose
    >
      <Form
        style={{
          display: "flex",
          gap: "10px",
          flexDirection: "column",
        }}
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <InputWrapper label="Title" required>
          <Form.Item
            name="title"
            rules={[
              { required: true, message: "Title is required!" },
              { validator: validateForm },
            ]}
          >
            <CustomInput autoComplete="off" placeholder="Enter course title" />
          </Form.Item>
        </InputWrapper>

        <InputWrapper label="Description" required>
          <Form.Item
            name="description"
            rules={[
              { required: true, message: "Description is required!" },
              { validator: validateForm },
            ]}
          >
            <CustomInput autoComplete="off" placeholder="Enter description" />
          </Form.Item>
        </InputWrapper>
        <InputWrapper label="Owner ID" required>
          <Form.Item
            name="ownerId"
            initialValue={1}
            rules={[
              { required: true, message: "Owner ID is required!" },
              { validator: validateForm },
            ]}
          >
            <CustomInput autoComplete="off" placeholder="Enter owner ID" disabled={true}/>
          </Form.Item>
        </InputWrapper>

        <InputWrapper label="Course Level" required>
          <Form.Item
            name="courseLevel"
            rules={[
              { required: true, message: "Course level is required!" },
              { validator: validateForm },
            ]}
          >
            <CustomSelector
              className="course-level-selector"
              placeholder="Select course level"
              options={[
                { value: "BEGINNER", label: "Beginner" },
                { value: "INTERMEDIATE", label: "Intermediate" },
                { value: "PROFESSIONAL", label: "Professional" },
              ]}
            />
          </Form.Item>
        </InputWrapper>

        <InputWrapper label="Status" required>
          <Form.Item
            name="Status"
            rules={[
              { required: true, message: "Active status is required!" },
              { validator: validateForm },
            ]}
          >
            <CustomSelector
              className="status-selector"
              placeholder="Select status"
              options={[
                { value: "true", label: "Active" },
                { value: "false", label: "InActive" },
              ]}
            />
          </Form.Item>
        </InputWrapper>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            width: "100%",
            marginTop: "10px",
          }}
        >
          <CustomButton
            style={{
              width: "50%",
            }}
            type="primary"
            onClick={() => form.submit()}
          >
            {selectedCourse ? "Edit" : "Add"}
          </CustomButton>
          <CustomButton
            style={{
              width: "50%",
            }}
            variant="outlined"
            onClick={handleCloseModal}
          >
            Cancel
          </CustomButton>
        </div>
      </Form>
    </CustomModal>
  );
};

export default CoursesModal;
